import type Phaser from "phaser";

import type { PlayerPickupFeedback } from "../application/player-runtime";
import { foodVisualConfig } from "../config/food-visual-config";
import type { FoodId } from "../config/gameplay-config";
import type { MotionProfile } from "../config/motion-config";
import type { FoodEntity } from "../simulation/food-population";
import type { PlayerMovementState } from "../simulation/player-movement";

export interface FoodRenderInput {
  readonly feedback: PlayerPickupFeedback | null;
  readonly foods: readonly FoodEntity[];
  readonly motionProfile: MotionProfile;
  readonly player: PlayerMovementState | null;
  readonly runId: number;
  readonly simulationTimeMs: number;
}

export interface FoodAppearance {
  readonly cueAlpha: number;
  readonly scale: number;
}

export const getFoodAppearance = (
  variantId: FoodId,
  ageMs: number | null,
  simulationTimeMs: number,
  motionProfile: MotionProfile,
): FoodAppearance => {
  const idlePhase =
    (simulationTimeMs % foodVisualConfig.foodIdlePulseDurationMs) /
    foodVisualConfig.foodIdlePulseDurationMs;
  const idleScale =
    motionProfile.pulseScale === 1
      ? 1
      : 1 +
        Math.sin(idlePhase * Math.PI * 2) *
          foodVisualConfig.styles[variantId].idlePulseAmount;
  if (ageMs === null || ageMs >= foodVisualConfig.appearanceDurationMs) {
    return { cueAlpha: 0, scale: idleScale };
  }
  if (motionProfile.pulseScale === 1) {
    return { cueAlpha: 1, scale: 1 };
  }
  const progress = Math.max(0, ageMs) / foodVisualConfig.appearanceDurationMs;
  return {
    cueAlpha: 1 - progress,
    scale:
      foodVisualConfig.appearanceMinimumScale +
      (1 - foodVisualConfig.appearanceMinimumScale) * progress,
  };
};

const drawBerry = (
  graphics: Phaser.GameObjects.Graphics,
  food: FoodEntity,
  scale: number,
): void => {
  const style = foodVisualConfig.styles.berry;
  const radius = food.radius * scale;
  graphics.fillStyle(style.fillColor, 1);
  graphics.fillCircle(food.position.x - radius * 0.36, food.position.y, radius);
  graphics.fillCircle(food.position.x + radius * 0.36, food.position.y, radius);
  graphics.lineStyle(3, style.outlineColor, 1);
  graphics.strokeCircle(
    food.position.x - radius * 0.36,
    food.position.y,
    radius,
  );
  graphics.strokeCircle(
    food.position.x + radius * 0.36,
    food.position.y,
    radius,
  );
  graphics.fillStyle(style.accentColor, 1);
  graphics.fillTriangle(
    food.position.x,
    food.position.y - radius * 0.45,
    food.position.x + radius * 0.7,
    food.position.y - radius * 0.95,
    food.position.x + radius * 0.35,
    food.position.y - radius * 0.2,
  );
};

const drawCitrus = (
  graphics: Phaser.GameObjects.Graphics,
  food: FoodEntity,
  scale: number,
): void => {
  const style = foodVisualConfig.styles.citrus;
  const radius = food.radius * scale;
  graphics.fillStyle(style.fillColor, 1);
  graphics.fillTriangle(
    food.position.x,
    food.position.y - radius,
    food.position.x + radius,
    food.position.y,
    food.position.x,
    food.position.y + radius,
  );
  graphics.fillTriangle(
    food.position.x,
    food.position.y - radius,
    food.position.x - radius,
    food.position.y,
    food.position.x,
    food.position.y + radius,
  );
  graphics.lineStyle(3, style.outlineColor, 1);
  graphics.lineBetween(
    food.position.x,
    food.position.y - radius,
    food.position.x + radius,
    food.position.y,
  );
  graphics.lineBetween(
    food.position.x + radius,
    food.position.y,
    food.position.x,
    food.position.y + radius,
  );
  graphics.lineBetween(
    food.position.x,
    food.position.y + radius,
    food.position.x - radius,
    food.position.y,
  );
  graphics.lineBetween(
    food.position.x - radius,
    food.position.y,
    food.position.x,
    food.position.y - radius,
  );
  graphics.lineStyle(2, style.accentColor, 1);
  graphics.strokeCircle(food.position.x, food.position.y, radius * 0.36);
};

const drawMelon = (
  graphics: Phaser.GameObjects.Graphics,
  food: FoodEntity,
  scale: number,
): void => {
  const style = foodVisualConfig.styles.melon;
  const radius = food.radius * scale;
  graphics.fillStyle(style.fillColor, 1);
  graphics.fillEllipse(
    food.position.x,
    food.position.y,
    radius * 2,
    radius * 1.45,
  );
  graphics.lineStyle(4, style.outlineColor, 1);
  graphics.strokeEllipse(
    food.position.x,
    food.position.y,
    radius * 2,
    radius * 1.45,
  );
  graphics.lineStyle(3, style.accentColor, 1);
  for (const stripe of [-0.28, 0, 0.28]) {
    graphics.lineBetween(
      food.position.x + radius * stripe,
      food.position.y - radius * 0.52,
      food.position.x + radius * stripe,
      food.position.y + radius * 0.52,
    );
  }
};

const drawStarfruit = (
  graphics: Phaser.GameObjects.Graphics,
  food: FoodEntity,
  scale: number,
): void => {
  const style = foodVisualConfig.styles.starfruit;
  const radius = food.radius * scale;
  graphics.fillStyle(style.fillColor, 1);
  for (let point = 0; point < 5; point += 1) {
    const angle = (Math.PI * 2 * point) / 5 - Math.PI / 2;
    const nextAngle = angle + Math.PI / 5;
    graphics.fillTriangle(
      food.position.x,
      food.position.y,
      food.position.x + Math.cos(angle) * radius,
      food.position.y + Math.sin(angle) * radius,
      food.position.x + Math.cos(nextAngle) * radius * 0.48,
      food.position.y + Math.sin(nextAngle) * radius * 0.48,
    );
  }
  graphics.lineStyle(3, style.outlineColor, 1);
  graphics.strokeCircle(food.position.x, food.position.y, radius * 0.5);
  graphics.fillStyle(style.accentColor, 1);
  graphics.fillCircle(food.position.x, food.position.y, radius * 0.25);
};

const drawFood = (
  graphics: Phaser.GameObjects.Graphics,
  food: FoodEntity,
  appearance: FoodAppearance,
): void => {
  graphics.lineStyle(2, foodVisualConfig.foodHaloColor, 0.84);
  graphics.strokeCircle(
    food.position.x,
    food.position.y,
    food.radius * appearance.scale + foodVisualConfig.foodHaloGap,
  );
  if (appearance.cueAlpha > 0) {
    graphics.lineStyle(3, foodVisualConfig.foodHaloColor, appearance.cueAlpha);
    graphics.strokeCircle(
      food.position.x,
      food.position.y,
      food.radius + foodVisualConfig.foodHaloGap * 2,
    );
  }
  if (food.variantId === "berry") {
    drawBerry(graphics, food, appearance.scale);
  } else if (food.variantId === "citrus") {
    drawCitrus(graphics, food, appearance.scale);
  } else if (food.variantId === "melon") {
    drawMelon(graphics, food, appearance.scale);
  } else {
    drawStarfruit(graphics, food, appearance.scale);
  }
};

const drawPickupBurst = (
  graphics: Phaser.GameObjects.Graphics,
  input: FoodRenderInput,
): void => {
  if (input.feedback === null || input.player === null) {
    return;
  }
  const reducedMotion = input.motionProfile.particleOpacity === 0;
  const duration = reducedMotion
    ? foodVisualConfig.reducedMotionCueDurationMs
    : foodVisualConfig.pickupBurstDurationMs;
  const age = input.simulationTimeMs - input.feedback.startedAtMs;
  if (age < 0 || age >= duration) {
    return;
  }
  const progress = age / duration;
  const radius = reducedMotion
    ? foodVisualConfig.pickupBurstMinimumRadius
    : foodVisualConfig.pickupBurstMinimumRadius +
      (foodVisualConfig.pickupBurstMaximumRadius -
        foodVisualConfig.pickupBurstMinimumRadius) *
        progress;
  const alpha = reducedMotion ? 0.92 : 1 - progress;
  graphics.lineStyle(
    input.feedback.levelUp ? 6 : 4,
    foodVisualConfig.pickupBurstColor,
    alpha,
  );
  graphics.strokeCircle(
    input.player.position.x,
    input.player.position.y,
    radius,
  );
  const rayCount = input.feedback.levelUp ? 8 : 6;
  for (let ray = 0; ray < rayCount; ray += 1) {
    const angle = (Math.PI * 2 * ray) / rayCount;
    const inner = radius + 4;
    const outer = radius + (reducedMotion ? 8 : 14);
    graphics.lineBetween(
      input.player.position.x + Math.cos(angle) * inner,
      input.player.position.y + Math.sin(angle) * inner,
      input.player.position.x + Math.cos(angle) * outer,
      input.player.position.y + Math.sin(angle) * outer,
    );
  }
};

export class FoodRenderer {
  private readonly appearanceStartedAt = new Map<string, number>();
  private knownFoodIds = new Set<string>();
  private runId: number | null = null;

  public constructor(private readonly graphics: Phaser.GameObjects.Graphics) {}

  public destroy(): void {
    this.appearanceStartedAt.clear();
    this.knownFoodIds.clear();
    this.graphics.destroy();
  }

  public render(input: FoodRenderInput): void {
    this.graphics.clear();
    if (this.runId !== input.runId) {
      this.runId = input.runId;
      this.knownFoodIds = new Set(input.foods.map((food) => food.id));
      this.appearanceStartedAt.clear();
    }
    const activeIds = new Set(input.foods.map((food) => food.id));
    for (const id of this.knownFoodIds) {
      if (!activeIds.has(id)) {
        this.knownFoodIds.delete(id);
        this.appearanceStartedAt.delete(id);
      }
    }
    for (const food of input.foods) {
      if (!this.knownFoodIds.has(food.id)) {
        this.knownFoodIds.add(food.id);
        this.appearanceStartedAt.set(food.id, input.simulationTimeMs);
      }
      const startedAt = this.appearanceStartedAt.get(food.id);
      const age =
        startedAt === undefined ? null : input.simulationTimeMs - startedAt;
      drawFood(
        this.graphics,
        food,
        getFoodAppearance(
          food.variantId,
          age,
          input.simulationTimeMs,
          input.motionProfile,
        ),
      );
      if (age !== null && age >= foodVisualConfig.appearanceDurationMs) {
        this.appearanceStartedAt.delete(food.id);
      }
    }
    drawPickupBurst(this.graphics, input);
  }
}
